import { useEffect, useState } from 'react';
import { call, getToken, SIGNED_IN_EVENT } from './api.js';
import { go } from './nav.js';
import { needsAccount } from './gate.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * "DID YOU BOOK AT ___?" — THE NEXT TIME THEY OPEN ELEAT.
 *
 * Your blueprint, 12 June 2026, and Al-Noor again on 21 Oct 2026: somebody who
 * went to the restaurant's own booking page and never came back to answer is
 * asked once, the next time Eleat opens. Their answer is what the restaurant
 * sees on its desk (Requested), and what Analytics counts.
 *
 * Only a hand-off to the restaurant's own page (never a booking Eleat made or
 * a connected app reports by itself), only in the last 7 days, once a visit,
 * and at most three times for one booking — never a nag.
 * ══════════════════════════════════════════════════════════════════════════ */

const SEEN = 'eleat.bookAskVisit';
const TIMES = 'eleat.bookAskTimes';
const WEEK = 7 * 24 * 3600 * 1000;

function asked() { try { return JSON.parse(localStorage.getItem(TIMES) || '{}'); } catch { return {}; } }
function countAsk(id) {
  try { const a = asked(); a[id] = (a[id] || 0) + 1; localStorage.setItem(TIMES, JSON.stringify(a)); } catch { /* refused */ }
}

function BookingAsk() {
  const [r, setR] = useState(null);

  useEffect(() => {
    const look = () => {
      if (!getToken()) return;
      try { if (sessionStorage.getItem(SEEN)) return; sessionStorage.setItem(SEEN, '1'); } catch { /* refused */ }
      /* never on top of the booking screen itself */
      if (new URLSearchParams(window.location.search).has('reserve')) return;
      call('/reservations/mine', { auth: true })
        .then((d) => {
          const rows = Array.isArray(d) ? d : ((d && (d.reservations || d.results)) || []);
          const a = asked();
          const due = rows.find((x) => x.outcome === 'pending' && !x.is_eleat_booking && !x.booked_in_eleat
            && !x.status && x.click_out_at && Date.now() - new Date(x.click_out_at).getTime() < WEEK
            && (a[x.id] || 0) < 3);
          if (due) { countAsk(due.id); setR(due); }
        })
        .catch(() => {});
    };
    look();
    window.addEventListener(SIGNED_IN_EVENT, look);
    return () => window.removeEventListener(SIGNED_IN_EVENT, look);
  }, []);

  if (!r) return null;
  const answer = (outcome) => {
    const d = r;
    setR(null);
    call(`/reservations/${encodeURIComponent(d.id)}/outcome`, { method: 'POST', auth: true, body: { outcome } })
      .catch(() => {});
    /* Yes → "When for, and how many?" — the date is when "Did you make it?" is asked */
    if (outcome === 'yes' && d.restaurant_id) {
      go(`?reserve=${encodeURIComponent(d.restaurant_id)}&details=${encodeURIComponent(d.id)}`);
    }
  };
  return (
    <div className="egate" role="dialog" aria-modal="true" aria-labelledby="bookaskH">
      <div className="egate-veil" onClick={() => setR(null)} />
      <div className="egate-card">
        <div className="egate-grip" />
        {/* Al-Noor, 3 Nov 2026: "Did you make a Reservation" — Yes and No; Yes asks the date and time */}
        <h3 className="egate-h" id="bookaskH">{`Did you make a reservation at ${r.restaurant_name}?`}</h3>
        <button className="egate-go" type="button" onClick={() => answer('yes')}>Yes</button>
        <button className="egate-no" type="button" onClick={() => answer('no')}>No</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * "DID YOU MAKE IT TO ___?" — AFTER THE BOOKING TIME (227)
 *
 * Al-Noor, 3 Nov 2026: "Yes, I reserved do it." The day after the booking,
 * around lunch on the venue's clock, Eleat asks once (the bell and, by their
 * settings, email) and reminds once 3 days later (review.ask.js). Here:
 *   · `?dined=<booking>` — the email's button and the bell — asks at once;
 *   · otherwise the next open asks about a booking Eleat already asked about
 *     and they have not answered since;
 *   · Yes → their review sheet on the restaurant page (`&write=1`);
 *     No → never asked again. (Yes and No only — Al-Noor, 3 Nov 2026.)
 *   · `?stopasks=<token>` — the email's "Stop these emails" link.
 * ══════════════════════════════════════════════════════════════════════════ */
const DINED_SEEN = 'eleat.dinedAskVisit';

export function DinedSheet({ venue, answer, close }) {
  return (
    <div className="egate" role="dialog" aria-modal="true" aria-labelledby="dinedH">
      <div className="egate-veil" onClick={close} />
      <div className="egate-card">
        <div className="egate-grip" />
        <h3 className="egate-h" id="dinedH">{`Did you make it to ${venue}?`}</h3>
        {/* Yes and No only (Al-Noor, 3 Nov 2026) — the date is known, so no "Not yet" */}
        <button className="egate-go" type="button" onClick={() => answer('yes')}>Yes</button>
        <button className="egate-no" type="button" onClick={() => answer('no')}>No</button>
      </div>
    </div>
  );
}

function DinedAsk() {
  const [r, setR] = useState(null);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const stop = q.get('stopasks');
    if (stop) {
      call('/reservations/review-asks/stop', { method: 'POST', body: { token: stop } })
        .then(() => setStopped(true)).catch(() => {});
      return undefined;
    }
    const look = () => {
      const named = new URLSearchParams(window.location.search).get('dined');
      if (named) {
        if (needsAccount('review', { ret: `?dined=${encodeURIComponent(named)}` })) return;
        call(`/reservations/${encodeURIComponent(named)}/dined`, { auth: true })
          .then((d) => {
            if (!d) return;
            if (d.dined_self_reported === true) go(`?venue=${encodeURIComponent(d.restaurant_id)}&tab=reviews&write=1`, { replace: true });
            else if (d.dined_self_reported == null && d.time_passed) setR(d);
          })
          .catch(() => {});
        return;
      }
      if (!getToken()) return;
      try { if (sessionStorage.getItem(DINED_SEEN)) return; sessionStorage.setItem(DINED_SEEN, '1'); } catch { /* refused */ }
      if (q.has('reserve') || q.has('venue')) return;
      call('/reservations/dined-due', { auth: true })
        .then((d) => {
          /* never on top of the "Did you book?" sheet */
          if (d && d.due && !document.querySelector('.egate')) setR(d.due);
        })
        .catch(() => {});
    };
    look();
    window.addEventListener(SIGNED_IN_EVENT, look);
    return () => window.removeEventListener(SIGNED_IN_EVENT, look);
  }, []);

  if (stopped) {
    return (
      <div className="egate" role="dialog" aria-modal="true" aria-labelledby="stopH">
        <div className="egate-veil" onClick={() => { setStopped(false); go('?', { replace: true }); }} />
        <div className="egate-card">
          <div className="egate-grip" />
          <h3 className="egate-h" id="stopH">You won’t get these emails again.</h3>
          <button className="egate-go" type="button" onClick={() => { setStopped(false); go('?', { replace: true }); }}>OK</button>
        </div>
      </div>
    );
  }
  if (!r) return null;
  const answer = (a) => {
    const d = r;
    setR(null);
    call(`/reservations/${encodeURIComponent(d.id)}/dined`, { method: 'POST', auth: true, body: { answer: a } })
      .then((out) => {
        if (a === 'yes' && out && out.can_review) {
          go(`?venue=${encodeURIComponent(d.restaurant_id)}&tab=reviews&write=1`);
        }
      })
      .catch(() => {});
  };
  return <DinedSheet venue={r.restaurant_name} answer={answer} close={() => setR(null)} />;
}

export default function BookAsk() {
  return (
    <>
      <BookingAsk />
      <DinedAsk />
    </>
  );
}
